import { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Card } from 'react-bootstrap';
import { FaSync, FaTachometerAlt, FaCalendarCheck, FaClipboardList, FaUsers, FaBell } from 'react-icons/fa';
import moment from 'moment';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import QuickStatsRow from '../components/dashboard/QuickStatsRow';
import AttendanceWidget from '../components/dashboard/AttendanceWidget';
import LeaveWidget from '../components/dashboard/LeaveWidget';
import EmployeeWidget from '../components/dashboard/EmployeeWidget';
import HRSoundToggle from '../components/dashboard/HRSoundToggle';
import UpcomingReminders from '../components/dashboard/UpcomingReminders';
import PendingApprovalsWidget from '../components/dashboard/PendingApprovalsWidget';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import DepartmentFilter from '../components/dashboard/DepartmentFilter';
import EmployeeDashboardPage from './EmployeeDashboardPage';
import { getDashboardStats, getRecentActivities, getAttendanceOverview } from '../services/firebase/dashboardService';
import '../assets/css/dashboard.css';

const DashboardPage = () => {
  const { user } = useAuth();

  // Role-based check - must be before any conditional returns but after hooks
  const isPrivilegedUser = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';

  // All hooks must be called unconditionally at the top level
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [dateRange, setDateRange] = useState({
    start: moment().format('YYYY-MM-DD'),
    end: moment().format('YYYY-MM-DD')
  });
  const [selectedDepartment, setSelectedDepartment] = useState('');

  const fetchDashboardData = useCallback(async () => {
    if (!isPrivilegedUser) return; // Don't fetch if not privileged

    setLoading(true);
    try {
      const filterOptions = {
        department: selectedDepartment,
        startDate: dateRange.start,
        endDate: dateRange.end
      };
      const [statsRes, activitiesRes, overviewRes] = await Promise.all([
        getDashboardStats(filterOptions),
        getRecentActivities(),
        getAttendanceOverview(filterOptions)
      ]);

      const stats = statsRes.data || {};
      // activitiesRes and overviewRes available for future use

      const formattedData = {
        quickStats: {
          totalEmployees: stats.totalEmployees || 0,
          presentToday: stats.presentToday || 0,
          onLeave: stats.onLeaveToday || 0,
          pendingApprovals: (stats.wrmsStats?.leave?.pending || 0) +
                            (stats.wrmsStats?.early_leave?.pending || 0) +
                            (stats.wrmsStats?.gate_pass?.pending || 0) +
                            (stats.wrmsStats?.extra_work?.pending || 0),
          attendancePercentage: stats.totalEmployees ? Math.round((stats.presentToday / stats.totalEmployees) * 100) : 0
        },
        attendance: {
          today: {
            present: stats.presentToday || 0,
            absent: stats.absentToday || 0,
            leave: stats.onLeaveToday || 0,
            late: stats.lateToday || 0,
            percentage: stats.totalEmployees ? Math.round((stats.presentToday / stats.totalEmployees) * 100) : 0
          },
          trend: stats.attendanceTrend || []
        },
        leaves: {
          pending: stats.pendingLeaves || 0,
          approved: stats.approvedLeaves || 0,
          rejected: stats.rejectedLeaves || 0,
          currentlyOnLeave: stats.currentlyOnLeave || []
        },
        employees: {
          total: stats.totalEmployees || 0,
          active: stats.active || 0,
          newJoinings: stats.newJoinings || 0,
          resignations: stats.resignations || 0,
          probation: stats.probation || 0,
          departments: stats.totalDepartments || 0,
          byDepartment: stats.byDepartment || []
        },
        approvals: {
          wrmsPending: {
            leave: stats.wrmsStats?.leave?.pending || 0,
            early_leave: stats.wrmsStats?.early_leave?.pending || 0,
            gate_pass: stats.wrmsStats?.gate_pass?.pending || 0,
            extra_work: stats.wrmsStats?.extra_work?.pending || 0
          },
          documents: stats.incompleteProfiles || 0,
          overdue: []
        },
        wrmsStats: stats.wrmsStats || {}
      };

      setDashboardData(formattedData);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [isPrivilegedUser, selectedDepartment, dateRange]);

  useEffect(() => {
    if (isPrivilegedUser) {
      fetchDashboardData();
    }
  }, [fetchDashboardData, isPrivilegedUser]);

  useEffect(() => {
    if (!isPrivilegedUser) return;

    const interval = setInterval(() => {
      fetchDashboardData();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchDashboardData, isPrivilegedUser]);

  // Conditional render AFTER all hooks
  if (!isPrivilegedUser) {
    return <EmployeeDashboardPage />;
  }

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
  };

  const handleDepartmentChange = (dept) => {
    setSelectedDepartment(dept);
  };

  return (
    <div className="d-flex dashboard-page" style={{ width: '100%', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 dashboard-main-content">
        <Navbar />
        <Container fluid className="py-3 py-md-4 px-2 px-md-4">

          {/* Filtering & Quick Stats */}
          <Row className="mb-3 mb-md-4">
            <Col xs={12} className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3">
              <h4 className="fw-bold text-dark mb-0 fs-5 fs-md-4">Organizational Overview</h4>
              <div className="d-flex flex-wrap align-items-center gap-2 w-100 w-md-auto">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="fw-bold border-0 shadow-sm px-3 py-1 rounded-pill d-flex align-items-center"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <FaSync className={`me-2 ${loading ? 'fa-spin' : ''}`} />
                  <span>{loading ? 'Syncing...' : 'Sync Data'}</span>
                </Button>
                <div className="vr d-none d-md-block" style={{ height: '24px' }}></div>
                <div className="flex-grow-1 flex-md-grow-0">
                  <DepartmentFilter
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                  />
                </div>
                <div className="flex-grow-1 flex-md-grow-0">
                  <DateRangePicker onChange={handleDateRangeChange} />
                </div>
              </div>
            </Col>
            <Col xs={12}>
              <QuickStatsRow
                stats={dashboardData?.quickStats}
                loading={loading}
              />
            </Col>
          </Row>

          {loading && !dashboardData ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Intelligent engine analyzing HRM data...</p>
            </div>
          ) : (
            <Row className="g-3 g-md-4">
              {/* Left Column: Analytics & Trends */}
              <Col xs={12} lg={8}>
                <Card className="widget-card mb-3 mb-md-4">
                  <Card.Header>
                    <h5 className="card-title text-primary">
                      <FaCalendarCheck /> Attendance Overview
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <AttendanceWidget
                      data={dashboardData?.attendance}
                      dateRange={dateRange}
                      department={selectedDepartment}
                    />
                  </Card.Body>
                </Card>

                <Row className="g-3 g-md-4">
                  <Col xs={12} md={6}>
                    <Card className="widget-card h-100">
                      <Card.Header>
                        <h5 className="card-title text-success">
                          <FaClipboardList /> Leave & Work Requests
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <LeaveWidget stats={dashboardData?.wrmsStats} userRole={user?.role} />
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card className="widget-card h-100">
                      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="card-title text-info mb-0">
                          <FaUsers /> Workforce Composition
                        </h5>
                        <HRSoundToggle user={user} />
                      </Card.Header>
                      <Card.Body>
                        <EmployeeWidget data={dashboardData?.employees} />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>

              {/* Right Column: Actions & Notifications */}
              <Col xs={12} lg={4}>
                <Card className="widget-card mb-3 mb-md-4 border-top border-5 border-warning">
                  <Card.Header>
                    <h5 className="card-title text-warning">
                      <FaBell /> Critical Approvals
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <PendingApprovalsWidget data={dashboardData?.approvals} />
                  </Card.Body>
                </Card>

                <Card className="widget-card mb-3 mb-md-4">
                  <Card.Header>
                    <h5 className="card-title text-dark">
                      Upcoming Reminders
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <UpcomingReminders />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </div>
  );
};

export default DashboardPage;
